import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Tooltip, DatePicker, Select } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import AddWfh from "./AddWfh";
import DataParser from "../../../utlits/Dataparser";
import WfhService from "../../../Services/WfhService";
import SocketService from "../../../Services/SocketService";
import shortid from "shortid";
import TableSkeleton from "../../Reuseable/TableSkeleton";

const WfhApplications = () => {
    const { RangePicker } = DatePicker;
    const { Option } = Select;

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Filters
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateRangeFilter, setDateRangeFilter] = useState(null);

    // Detail Modal States
    const [selectedApp, setSelectedApp] = useState(null);

    const companyId = DataParser.CompanyId();
    const userId = DataParser.UserId();

    const userRole = DataParser.userRoleTitle();
    const isHrOrAdmin = userRole === "HR" || userRole === "Company Admin" || userRole === "Sub Admin";

    const fetchApplications = async () => {
        if (!companyId || !userId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const params = { company_id: companyId };
            if (isHrOrAdmin) {
                params.user_id = userId;
            }

            const res = await WfhService.getAll(params);

            if (res.Good) {
                setApplications(res.data.wfh_applications || []);
            }
        } catch (err) {
            console.error("Error fetching WFH applications:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && userId) {
            fetchApplications();
        }

        const handleWfhUpdate = (data) => {
            if (String(data.company_id) === String(companyId)) {
                console.log("🔔 WFH update received via Socket:", data.action);
                fetchApplications();
            }
        };

        SocketService.on("wfh_update", handleWfhUpdate);

        return () => {
            SocketService.off("wfh_update", handleWfhUpdate);
        };
    }, [companyId, userId]);

    const handlePageChange = (e, page) => {
        e.preventDefault();
        setCurrentPage(page);
    };

    const handleViewDetails = (app) => {
        setSelectedApp(app);
        const modal = new window.bootstrap.Modal(document.getElementById("wfhDetailsModal"));
        modal.show();
    };

    const getFilteredData = () => {
        let filtered = [...applications];

        if (statusFilter !== "all") {
            filtered = filtered.filter(item => String(item.status) === statusFilter);
        }

        if (dateRangeFilter && dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            filtered = filtered.filter(item => {
                const itemStart = moment(item.from_date);
                const itemEnd = moment(item.to_date);
                return (
                    (itemStart.isSameOrAfter(start, 'day') && itemStart.isSameOrBefore(end, 'day')) ||
                    (itemEnd.isSameOrAfter(start, 'day') && itemEnd.isSameOrBefore(end, 'day')) ||
                    (itemStart.isBefore(start, 'day') && itemEnd.isAfter(end, 'day'))
                );
            });
        }

        return filtered;
    };

    const filteredData = getFilteredData();
    const paginatedData = paginate(filteredData, currentPage, pageSize);

    return (
        <>
            <Helmet>
                <title>My WFH Requests - Planit</title>
            </Helmet>

            <AddWfh
                id="addWfhModal"
                labelledby="addWfhModalLabel"
                refreshData={fetchApplications}
            />

            <div className="main-content">
                <style>{`
                    /* KILL ALL ROUNDED CORNERS AND SHADOWS */
                    .card, .card-body, .table-responsive, .listing-table-wrapper, 
                    .listing-table, .listing-table thead, .listing-table tr, 
                    .listing-table th, .listing-table td {
                        border-radius: 0 !important;
                        box-shadow: none !important;
                        border-left: none !important;
                        border-right: none !important;
                        border-top: none !important;
                    }

                    /* Match Evaluation Table exact geometry */
                    .listing-table thead tr.tableHeaderbg th {
                        background-color: #F2F2F2 !important;
                        color: #212529 !important;
                        border-bottom: none !important;
                        font-weight: 600 !important;
                        font-size: 13px !important;
                        padding: 8px 12px !important;
                    }

                    .listing-table tbody tr td {
                        border-bottom: 1px solid #F2F2F2 !important;
                        vertical-align: middle !important;
                    }

                    .listing-table tbody tr:last-child td {
                        border-bottom: none !important;
                    }

                    /* Filter Row Pattern Synced */
                    .custom-filter.float-select-wrap .ant-select-selector,
                    .custom-filter.float-select-wrap .ant-picker {
                        height: 34px !important;
                        min-height: 34px !important;
                        border-radius: 6px !important;
                    }
                    
                    .custom-filter.float-select-wrap .ant-select-selection-item,
                    .custom-filter.float-select-wrap .ant-select-selection-placeholder,
                    .custom-filter.float-select-wrap .ant-picker-input > input {
                        line-height: 30px !important;
                        font-size: 13px !important;
                    }

                    .custom-filter.float-select-wrap label {
                        font-size: 11px !important;
                        font-weight: 600 !important;
                    }

                    .ant-picker-range .ant-picker-active-bar {
                        background: #1D1D1D !important;
                    }

                    /* Hover/focus on filter fields */
                    .custom-filter.float-select-wrap:hover .ant-select-selector,
                    .custom-filter.float-select-wrap:focus-within .ant-select-selector {
                        border-color: #1D1D1D !important; box-shadow: none !important;
                    }
                    .custom-filter.float-select-wrap:hover .ant-picker,
                    .custom-filter.float-select-wrap .ant-picker-focused {
                        border-color: #1D1D1D !important; box-shadow: none !important;
                    }
                    .custom-filter.float-select-wrap:hover label,
                    .custom-filter.float-select-wrap:focus-within label { color: #1D1D1D !important; }
                    .custom-filter.float-select-wrap:hover .ant-select-arrow,
                    .custom-filter.float-select-wrap:focus-within .ant-select-arrow { color: #1D1D1D !important; }
                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div,
                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div { background-color: #1D1D1D !important; }
                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div i,
                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div i { color: #fff !important; }

                    .eval-action-btn {
                        height: 34px !important;
                        border-radius: 6px !important;
                        font-size: 13px !important;
                        font-weight: 600 !important;
                        padding: 0 15px !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        background-color: #0DDFAB !important;
                        border: 1px solid #0DDFAB !important;
                        color: white !important;
                    }

                    .eval-action-btn:hover {
                        background-color: #0bc698 !important;
                        border-color: #0bc698 !important;
                        color: white !important;
                    }
                `}</style>

                <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
                    <div className="container-fluid">
                        <div className="row px-0">
                            <div className="col-12 px-0">
                                <div className="d-flex align-items-center justify-content-end" style={{ padding: "0 0 12px 0" }}>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <div className={`float-select-wrap custom-filter ${statusFilter !== "all" ? "has-value" : ""}`} style={{ width: "180px", maxWidth: "180px" }}>
                                            <label>Status</label>
                                            <Select
                                                allowClear
                                                placeholder=""
                                                value={statusFilter === "all" ? null : statusFilter}
                                                onChange={(val) => setStatusFilter(val || "all")}
                                                dropdownStyle={{ zIndex: 1050 }}
                                            >
                                                <Option value="all" label="All">All</Option>
                                                <Option value="0" label="Pending">Pending</Option>
                                                <Option value="1" label="Approved">Approved</Option>
                                                <Option value="2" label="Disapproved">Disapproved</Option>
                                            </Select>
                                        </div>

                                        <div className={`float-select-wrap custom-filter ${dateRangeFilter ? "has-value" : ""}`} style={{ width: "280px", maxWidth: "280px" }}>
                                            <label>WFH Period</label>
                                            <RangePicker
                                                value={dateRangeFilter}
                                                onChange={setDateRangeFilter}
                                                format="DD/MM/YYYY"
                                                allowClear
                                                separator=""
                                                placeholder={['', '']}
                                                suffixIcon={
                                                    <div style={{
                                                        backgroundColor: "#F4F4F4",
                                                        borderRadius: "6px",
                                                        width: "24px",
                                                        height: "24px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginRight: "-4px"
                                                    }}>
                                                        <i className="uil-schedule" style={{ color: "#A0A0A0", fontSize: "15px" }} />
                                                    </div>
                                                }
                                            />
                                        </div>

                                        <button
                                            className="btn btn-dark eval-action-btn"
                                            data-bs-toggle="modal"
                                            data-bs-target="#addWfhModal"
                                        >
                                            <i className="uil-plus me-1"></i> Apply For WFH
                                        </button>
                                    </div>
                                </div>

                                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive listing-table-wrapper">
                                            <table className="table mb-0 listing-table custom-table">
                                                <thead>
                                                    <tr className="tableHeaderbg">
                                                        <th className="text-start ps-4">Start Date</th>
                                                        <th className="text-start">End Date</th>
                                                        <th className="text-start">Total Days</th>
                                                        <th className="text-start">Status</th>
                                                        <th className="text-start">Action Taken By</th>
                                                        <th className="text-center pe-4">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        <TableSkeleton cols={6} rows={6} />
                                                    ) : filteredData.length > 0 ? (
                                                        paginatedData.map((app) => (
                                                            <tr key={shortid.generate()} className="align-middle">
                                                                <td className="ps-4">
                                                                    <span><i className="uil uil-calendar-alt me-1" style={{ color: "#0DDFAB", fontSize: "14px" }}></i> {moment(app.from_date).format("D, MMMM")}</span>
                                                                </td>
                                                                <td>
                                                                    <span><i className="uil uil-calendar-alt me-1" style={{ color: "#0DDFAB", fontSize: "14px" }}></i> {moment(app.to_date).format("D, MMMM")}</span>
                                                                </td>
                                                                <td>{app.total_days}</td>
                                                                <td>
                                                                    {app.status === 0 ? (
                                                                        <span className="badge px-3" style={{ backgroundColor: "#fdfd96", color: "#856404", border: "1px solid #ced4da", borderRadius: "5px" }}>Pending</span>
                                                                    ) : app.status === 1 ? (
                                                                        <span className="badge px-3" style={{ backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb", borderRadius: "5px" }}>Approved</span>
                                                                    ) : (
                                                                        <span className="badge px-3" style={{ backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: "5px" }}>Disapproved</span>
                                                                    )}
                                                                </td>
                                                                <td>{app.status === 1 ? app.approved_by_user : (app.status === 2 ? app.rejected_by_user : "-")}</td>
                                                                <td className="text-center pe-4">
                                                                    <Link
                                                                        to="#"
                                                                        className="cursor-pointer iconscolor"
                                                                        onClick={() => handleViewDetails(app)}
                                                                    >
                                                                        <Tooltip title="View Details">
                                                                            <i className="uil-eye iconscolor" style={{ fontSize: "18px" }} />
                                                                        </Tooltip>
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="6" className="text-center py-4">No applications found.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            {filteredData.length > pageSize && (
                                                <div className="d-flex justify-content-center mt-4 pb-4">
                                                    <Pagination
                                                        itemCount={filteredData.length}
                                                        currentPage={currentPage}
                                                        pageSize={pageSize}
                                                        onPageChange={handlePageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details Modal */}
                <div className="modal fade" id="wfhDetailsModal" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content p-0" style={{ borderRadius: "12px", overflow: "hidden", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
                            <div className="modal-header border-0 px-4 py-3 bg-white pb-0">
                                <h5 className="modal-title fw-bold" style={{ color: "#1D1D1D", fontSize: "1.1rem" }}>WFH Application Details</h5>
                                <button type="button" className="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div className="modal-body p-4 bg-white">
                                {selectedApp && (
                                    <div className="leave-details">
                                        {[
                                            ["From", moment(selectedApp.from_date).format("DD/MM/YYYY")],
                                            ["To", moment(selectedApp.to_date).format("DD/MM/YYYY")],
                                            ["Total Days", <span className="fw-bold text-dark">{selectedApp.total_days}</span>],
                                            [
                                                "Status",
                                                selectedApp.status === 0 ? (
                                                    <span className="badge px-3" style={{ backgroundColor: "#fdfd96", color: "#856404", border: "1px solid #ced4da", borderRadius: "5px" }}>Pending</span>
                                                ) : selectedApp.status === 1 ? (
                                                    <span className="badge px-3" style={{ backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb", borderRadius: "5px" }}>Approved</span>
                                                ) : (
                                                    <span className="badge px-3" style={{ backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: "5px" }}>Disapproved</span>
                                                )
                                            ],
                                            ["Action Taken By", <span className="text-muted">{(selectedApp.status === 1 || selectedApp.status === 2) ? (selectedApp.approved_by_user || selectedApp.rejected_by_user || "-") : "-"}</span>],
                                            ...(selectedApp.status === 2 && selectedApp.rejection_reason ? [
                                                ["Rejection Note", <div style={{ color: "#c62828" }}>{selectedApp.rejection_reason}</div>]
                                            ] : []),
                                            ["Reason", <div style={{ fontSize: "0.9rem", color: "#666" }}>{selectedApp.reason}</div>]
                                        ].map(([label, value], idx, arr) => (
                                            <div key={idx} className={`mb-3 d-flex flex-wrap align-items-start ${idx === arr.length - 1 ? "" : "border-bottom"} pb-2`}>
                                                <div style={{ minWidth: "140px", fontWeight: "600", color: "#495057", fontSize: "0.9rem" }}>{label}:</div>
                                                <div className="flex-grow-1" style={{ color: "#212529", lineHeight: "1.5", wordBreak: "break-word", fontSize: "0.9rem" }}>{value}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WfhApplications;
